import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { NavLink, useSearchParams } from 'react-router';
import { supportedCodes } from '~/lib/languages';

const LINK_MAP: Record<string, string> = {
  '/privacy-policy/privacy-policy': '/legal/privacy',
  '/privacy-policy-jp/privacy-policy': '/legal/privacy',
  '/terms/fujitsu-launchpad-terms-of-service': '/legal/terms',
  '/terms-jp/fujitsu-launchpad-terms-of-service': '/legal/terms',
  '/privacy-policy/v2-1': '/legal/privacy/v2-1',
  '/privacy-policy/v2': '/legal/privacy/v2',
  '/privacy-policy/v1': '/legal/privacy/v1',
  '/privacy-policy-jp/v2-1': '/legal/privacy/v2-1',
  '/privacy-policy-jp/v2': '/legal/privacy/v2',
  '/privacy-policy-jp/v1': '/legal/privacy/v1',
  '/terms/v3': '/legal/terms/v3',
  '/terms/v2': '/legal/terms/v2',
  '/terms/v1': '/legal/terms/v1',
  '/terms-jp/v3': '/legal/terms/v3',
  '/terms-jp/v2': '/legal/terms/v2',
  '/terms-jp/v1': '/legal/terms/v1',
};

function rewriteHref(href: string | undefined): string {
  if (!href) return '#';
  const mapped = LINK_MAP[href];
  if (mapped) return mapped;
  if (href.startsWith('/') && !href.startsWith('//')) return `https://kotobase.io${href}`;
  return href;
}

function isExternal(href: string) {
  return /^https?:\/\//.test(href);
}

export function LegalProse({ content }: { content: string }) {
  const [searchParams] = useSearchParams();
  // Forward the active `?lang=` override (the highest-priority locale signal) so
  // navigating between legal docs keeps the reader in their chosen language even
  // before a `lang` cookie is set.
  const langParam = searchParams.get('lang');
  const search = langParam && supportedCodes.includes(langParam) ? `?lang=${langParam}` : '';

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1({ children }) {
          return <h1 className="mt-2 mb-6 text-h1">{children}</h1>;
        },
        h2({ children }) {
          return <h2 className="mt-8 mb-4 text-h2">{children}</h2>;
        },
        h3({ children }) {
          return <h3 className="mt-6 mb-3 text-h3">{children}</h3>;
        },
        h4({ children }) {
          return <h4 className="mt-4 mb-2 text-body-big-bold">{children}</h4>;
        },
        p({ children }) {
          return <p className="mb-4 text-body-big leading-relaxed">{children}</p>;
        },
        a({ href, children }) {
          const rewritten = rewriteHref(href);
          if (isExternal(rewritten)) {
            return (
              <a
                href={rewritten}
                className="text-primary underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          }
          return (
            <NavLink to={{ pathname: rewritten, search }} className="text-primary underline">
              {children}
            </NavLink>
          );
        },
        ul({ children }) {
          return <ul className="mb-4 ml-6 list-disc space-y-1">{children}</ul>;
        },
        ol({ children }) {
          return <ol className="mb-4 ml-6 list-decimal space-y-1">{children}</ol>;
        },
        li({ children }) {
          return <li className="text-body-big">{children}</li>;
        },
        strong({ children }) {
          return <strong className="font-semibold">{children}</strong>;
        },
        hr() {
          return <hr className="border-border-300 my-6" />;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
